import { basicInfoAtom, itemOffersAtom, itemsAtom, optionGroupsAtom } from "@/atoms";
import cartAtom, { createEmptyCart, initialValueCart } from "@/atoms/cart";
import cartHydratedAtom from "@/atoms/cart-hydrated";
import cartInitializerAtom from "@/atoms/cart-initializer";
import categoriesAtom from "@/atoms/categories";
import { lastSyncedCartAtom } from "@/atoms/last-synced-cart";
import { cartSyncEnabled } from "@/consts";
import { cartBackendService } from "@/lib/cart-backend-service";
import {
  CART_TTL_MS,
  clearLocalCart,
  loadLocalCart,
  readCartUpdatedAt,
  saveLocalCart,
  touchCartTimestamp,
} from "@/lib/cart-local-storage";
import { sumCartItems } from "@/lib/pricing";
import { Cart } from "@/types";
import { useAtom, useAtomValue } from "jotai";
import { useCallback, useRef } from "react";
import { useAuthStatus } from "./use-auth-status";

let _cartInitialized = false;

export function resetCartInitialized() {
  _cartInitialized = false;
}

export function useCart() {
  const [cart, setCart] = useAtom(cartAtom);
  const [, setLastSyncedCart] = useAtom(lastSyncedCartAtom);
  const items = useAtomValue(itemsAtom);
  const itemOffers = useAtomValue(itemOffersAtom);
  const optionGroups = useAtomValue(optionGroupsAtom);
  const basicInfo = useAtomValue(basicInfoAtom);
  const categories = useAtomValue(categoriesAtom);
  const [, setCartInitializer] = useAtom(cartInitializerAtom);
  const [, setCartHydrated] = useAtom(cartHydratedAtom);
  const isAuthenticated = useAuthStatus();
  const shouldUseRemoteCart = cartSyncEnabled && isAuthenticated;
  const hasLoadedCart = useRef(false);

  const updateCart = useCallback(
    (newCart: Cart | ((prev: Cart) => Cart)) => {
      setCart((prevCart) => {
        const updatedCart = typeof newCart === "function" ? newCart(prevCart) : newCart;
        const removedItemsQuantityZero = updatedCart.items.filter((itemCart) => itemCart.quantity !== 0);

        const finalCart = {
          ...updatedCart,
          items: removedItemsQuantityZero,
          totalPrice: sumCartItems(removedItemsQuantityZero),
        };

        if (!shouldUseRemoteCart) {
          try {
            saveLocalCart(finalCart);
          } catch (error) {
            console.error("Erro ao salvar carrinho no localStorage:", error);
          }
        } else {
          touchCartTimestamp();
        }

        return finalCart;
      });
    },
    [shouldUseRemoteCart, setCart]
  );

  const clearCart = useCallback(async () => {
    const emptyCart = createEmptyCart();
    setCart(emptyCart);

    try {
      if (shouldUseRemoteCart) {
        await cartBackendService.clearCart();
        setLastSyncedCart(emptyCart);
      } else {
        clearLocalCart();
        setLastSyncedCart(null);
      }
    } catch (error) {
      console.error("Erro ao limpar carrinho:", error);
      if (shouldUseRemoteCart) {
        clearLocalCart();
      }
    }
  }, [shouldUseRemoteCart, setCart, setLastSyncedCart]);

  const loadInitialCart = useCallback(
    async (force = false) => {
      if (_cartInitialized && !force) return;
      try {
        if (shouldUseRemoteCart) {
          const updatedAt = readCartUpdatedAt();
          if (updatedAt !== null && Date.now() - updatedAt > CART_TTL_MS) {
            await cartBackendService.clearCart();
            clearLocalCart();
            setCart(createEmptyCart());
            setLastSyncedCart(initialValueCart);
            return;
          }

          const backendCart = await cartBackendService.loadCart({
            items,
            itemOffers,
            optionGroups,
            basicInfo,
          });
          if (backendCart && backendCart.items.length > 0) touchCartTimestamp();

          const localCart = loadLocalCart();

          if (localCart.items.length > 0 && backendCart && backendCart.items.length > 0) {
            setCartInitializer(true);
            return;
          }

          if (backendCart && backendCart.items.length > 0) {
            setCart(backendCart);
            setLastSyncedCart(JSON.parse(JSON.stringify(backendCart)));
          } else {
            if (localCart.items.length > 0) {
              await cartBackendService.saveCart(localCart, categories);

              const reloadedCart = await cartBackendService.loadCart({
                items,
                itemOffers,
                optionGroups,
                basicInfo,
              });

              if (reloadedCart) {
                setCart(reloadedCart);
                setLastSyncedCart(JSON.parse(JSON.stringify(reloadedCart)));
              } else {
                setCart(localCart);
              }

              clearLocalCart();
            } else {
              setLastSyncedCart(initialValueCart);
            }
          }
        } else {
          const localCart = loadLocalCart();
          if (localCart.items.length > 0) {
            setCart(localCart);
          }
          setLastSyncedCart(null);
        }
      } catch (error) {
        const localCart = loadLocalCart();
        if (localCart.items.length > 0) {
          setCart(localCart);
        }
        setLastSyncedCart(initialValueCart);
      } finally {
        _cartInitialized = true;
        hasLoadedCart.current = true;
        setCartHydrated(true);
      }
    },
    [
      shouldUseRemoteCart,
      items,
      itemOffers,
      optionGroups,
      basicInfo,
      categories,
      setCart,
      setCartInitializer,
      setCartHydrated,
      setLastSyncedCart,
    ]
  );

  return {
    cart,
    updateCart,
    clearCart,
    isAuthenticated,
    loadInitialCart,
    clearLocalStorage: clearLocalCart,
    loadFromLocalStorage: loadLocalCart,
  };
}
